export interface VehiclePriceRow {
  id: string;
  label: string;
  amount: number;
  isDiscount: boolean;
}

export interface VehiclePriceRows {
  rows: VehiclePriceRow[];
  showIncentives: boolean;
}

type Amount = number | null | undefined;

export interface VehiclePriceInput {
  msrp: Amount;
  vehicleStickerPrice: Amount;
  carvanaDiscount: Amount;
  tradeBoostAmountSaved: Amount;
  creditKarmaAmountSaved: Amount;
  employeeDiscountAmount: Amount;
  priceReductions: Amount;
  totalIncentives: Amount;
}

/**
 * The no-discounts design collapses the vehicle price to one net "Vehicle
 * price" row; the max design itemizes instead — gross "List price"/"MSRP" followed
 * by one green row per discount, with no intermediate net subtotal before Sales tax.
 *
 * Discount order follows the max frame (Bundle & Save, Price reductions, Employee
 * discount). "Customer discount" and "Manufacturer incentives" are new-car-only and
 * have no frame of their own — they keep control's placement from price-breakdown.tsx.
 */
export const deriveVehiclePriceRows = ({
  msrp,
  vehicleStickerPrice,
  carvanaDiscount,
  tradeBoostAmountSaved,
  creditKarmaAmountSaved,
  employeeDiscountAmount,
  priceReductions,
  totalIncentives
}: VehiclePriceInput): VehiclePriceRows => {
  const isNewCar = (msrp ?? 0) > 0;
  const sticker = vehicleStickerPrice ?? 0;
  const incentives = totalIncentives ?? 0;
  const showIncentives = isNewCar && incentives > 0;

  const discounts = [
    { id: 'customer', label: 'Customer discount', amount: isNewCar ? carvanaDiscount ?? 0 : 0 },
    { id: 'bundle', label: 'Bundle & Save discount', amount: tradeBoostAmountSaved ?? 0 },
    { id: 'price-reductions', label: 'Price reductions', amount: priceReductions ?? 0 },
    { id: 'employee', label: 'Employee discount', amount: employeeDiscountAmount ?? 0 },
    { id: 'credit-karma', label: 'Credit Karma savings', amount: creditKarmaAmountSaved ?? 0 }
  ]
    .filter(row => row.amount > 0)
    .map(row => ({ ...row, isDiscount: true }));

  if (!discounts.length && !isNewCar) {
    return {
      rows: [
        {
          id: 'net',
          label: 'Vehicle price',
          amount: sticker - incentives,
          isDiscount: false
        }
      ],
      showIncentives
    };
  }

  return {
    rows: [
      {
        id: 'gross',
        label: isNewCar ? 'MSRP' : 'List price',
        amount: isNewCar ? msrp ?? 0 : sticker,
        isDiscount: false
      },
      ...discounts
    ],
    showIncentives
  };
};

export default deriveVehiclePriceRows;
